import { clsx } from 'clsx'
import { type FC } from 'react'

import { IconCaretRight } from '@redteclab/icons'

import { Link } from 'base-ui'

import { apmCaptureError } from '../../../apm/apmCaptureError'
import { apmSetCustomContext } from '../../../apm/apmSetCustomContext'
import { useDeviceDetectorContext } from '../../../device-detector/context/deviceDetectorContext'
import { useGlobalConfigContext } from '../../../global-config/context/globalConfigContext'
import { type SeoBreadcrumbListItem } from '../../../seo/components/SeoBreadcrumbListJsonLd'
import {
  EXPERIMENTS_TRACK_EVENT_BREADCRUMBS,
  experimentsTrackEvent,
} from '../../eventTracking'
import { useGetExperiment } from '../../hooks'
import { EXPERIMENT_BREADCRUMBS } from '../../model/EXPERIMENT_NAME_PER_TENANT'
import {
  experimentAccessorGetExperimentNamePerTenant,
  experimentAccessorNonDefault,
} from '../../model/experimentAccessor'

const useBreadcrumbsExperiment = (
  experimentName: string | undefined,
): boolean => {
  const breadcrumbsExperiment = useGetExperiment(experimentName)

  return experimentAccessorNonDefault(breadcrumbsExperiment)
}

export const ExperimentBreadcrumbs: FC<{
  experimentBreadCrumbs: {
    desktop: SeoBreadcrumbListItem[]
    mobile: SeoBreadcrumbListItem[]
  } | null
}> = ({ experimentBreadCrumbs }) => {
  const { isMobile } = useDeviceDetectorContext()

  const experimentBreadCrumbsToUse = isMobile
    ? experimentBreadCrumbs?.mobile
    : experimentBreadCrumbs?.desktop

  const globalConfig = useGlobalConfigContext()

  const { tenant } = globalConfig

  const experimentName = experimentAccessorGetExperimentNamePerTenant({
    experimentName: EXPERIMENT_BREADCRUMBS,
    isMobile,
    tenant,
  })
  const isBreadcrumbsExperimentEnabledAndNotDefault =
    useBreadcrumbsExperiment(experimentName)

  if (
    !isBreadcrumbsExperimentEnabledAndNotDefault ||
    !experimentBreadCrumbsToUse?.length
  ) {
    return null
  }

  return (
    <div className="container py-3">
      <div className="flex h-[1lh] items-center gap-2 text-dark-primary-strong desktop:flex-wrap desktop:overflow-hidden desktop:text-xs">
        {experimentBreadCrumbsToUse.map((breadcrumbItem, index) => {
          const link = breadcrumbItem.item
          const { name } = breadcrumbItem

          if (typeof link !== 'string' || typeof name !== 'string') {
            apmSetCustomContext({
              breadcrumbItem,
            })
            apmCaptureError(
              new Error('Breadcrumb item has a wrong format for experiment'),
            )

            return null
          }

          const first = index === 0
          const last = index === experimentBreadCrumbsToUse.length - 1

          return (
            <span
              className={clsx(
                'hidden items-center gap-2 whitespace-nowrap first:flex last:flex desktop:flex',
                last && 'max-w-full overflow-hidden',
              )}
              key={name}
            >
              {!first && <IconCaretRight className="size-4 shrink-0" />}
              <Link
                className={clsx(
                  'capitalize no-underline hover:underline focus:underline',
                  last && 'max-w-full overflow-hidden text-ellipsis',
                )}
                color="inherit"
                href={link}
                onClick={(): void => {
                  experimentsTrackEvent(
                    globalConfig,
                    EXPERIMENTS_TRACK_EVENT_BREADCRUMBS,
                  )
                }}
              >
                {name}
              </Link>
            </span>
          )
        })}
      </div>
    </div>
  )
}
